/* eslint-disable no-magic-numbers */

import {
  Button,
  dismissToast,
  notifyError,
  notifySuccess,
  notifyWarning,
  Table,
  type TableProps,
  useModal,
} from '@omnipresentgroup/design-system';
import type { XeroAccountCodeMappingItem } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { useManagerUser } from '../../hooks';
import { XeroCongratsModal, XeroTaxRatesMismatchModal } from './components';
import {
  ColumnHeaders,
  type TableMetaType,
  useAccountCodeMappings,
  useAccountCodesTableConfig,
} from './hooks';
import type { XeroAccountCodeMappingExtendedItem } from './types';

const EMPTY_ARRAY: XeroAccountCodeMappingItem[] = [];
const EMPTY_LOADING_ARRAY = Array.from(
  { length: 10 },
  () => ({}) as XeroAccountCodeMappingItem,
);

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

const SUCCESS_CHANGE_NOTIFICATION_MESSAGE = 'Your changes have been saved';
const ERROR_CHANGE_NOTIFICATION_MESSAGE = 'There are errors in your changes';
const SUCCESS_PARTIAL_CHANGE_NOTIFICATION_MESSAGE =
  'Not all the changes were saved successfully. Please review the changes and try again.';

const getTableTitle = (xeroOrganisation?: string) =>
  xeroOrganisation
    ? `Map your Xero accounts for ${xeroOrganisation}`
    : 'Map your Xero accounts';

const SAVE_BUTTON_LABEL = 'Save';

type Props = {
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders)[];
  optionsOverride?: Omit<
    TableOptions<XeroAccountCodeMappingItem>,
    'data' | 'columns'
  >;
  columnsOverride?: ColumnDef<XeroAccountCodeMappingItem, unknown>[];
  emptyStateProps?: TableProps['emptyStateProps'];
  tableActions?: TableProps['tableActions'];
  wrapperProps?: TableProps['wrapperProps'];
  xeroOrganisation?: string;
};

export const XeroAccountCodeMappingsList = ({
  columnVisibility,
  optionsOverride,
  columnsOverride,
  emptyStateProps,
  tableActions,
  wrapperProps,
  xeroOrganisation,
}: Props) => {
  const { modalProps } = useModal({});
  const [congratsModalOpen, setCongratsModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const location = useLocation();
  const fromPage = location.state?.from || 'xero';

  const { companyId } = useManagerUser();

  const {
    accountCodeMappings,
    changeXeroAccountCode,
    saveAccountCodeMappings,
    canSave,
    isFetchingAccountCodeMappings,
    isUpdatingAccountCodeMappings,
    hasErrorFetchingAccountCodeMappings,
  } = useAccountCodeMappings({
    companyId,
    onSaveSuccess: (status) => {
      switch (status) {
        case 'success': {
          notifySuccess({ title: SUCCESS_CHANGE_NOTIFICATION_MESSAGE });
          break;
        }
        case 'warning': {
          notifyWarning({ title: SUCCESS_PARTIAL_CHANGE_NOTIFICATION_MESSAGE });
          break;
        }
        case 'error': {
          notifyError({ title: ERROR_CHANGE_NOTIFICATION_MESSAGE });
          break;
        }
        default: {
          break;
        }
      }
    },
  });

  const [tableSorting, setTableSorting] = useState<SortingState>([]);

  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  /** Shows the congrats modal when all invoice categories have been mapped to Xero account codes */
  useEffect(() => {
    const shouldShowModal =
      fromPage === 'xero' &&
      Array.isArray(accountCodeMappings.dataRef) &&
      accountCodeMappings.dataRef.length > 0 &&
      accountCodeMappings.dataRef.every((item) =>
        Boolean(item?.xeroAccountCode),
      );

    if (shouldShowModal) {
      setCongratsModalOpen(true);
    }
  }, [accountCodeMappings.dataRef, fromPage]);

  const { columns } = useAccountCodesTableConfig({
    columnsOverride,
  });

  const updateXeroAccountCode: TableMetaType['updateXeroAccountCode'] =
    useCallback(
      (
        newXeroAccountCode: XeroAccountCodeMappingExtendedItem['xeroAccountCode'],
        rowIndex?: number,
      ) => {
        dismissToast();
        changeXeroAccountCode(newXeroAccountCode, rowIndex);
      },
      [changeXeroAccountCode],
    );

  const handleSaveData = useCallback(() => {
    const differentTaxRates = accountCodeMappings.dataChanged.some(
      (item) => item.xeroAccountCode?.taxRate !== item.category.taxRate,
    );
    if (differentTaxRates) {
      setConfirmationModalOpen(true);
    } else {
      saveAccountCodeMappings();
    }
  }, [accountCodeMappings.dataChanged, saveAccountCodeMappings]);

  const tableOptions: Omit<
    TableOptions<XeroAccountCodeMappingItem>,
    'data' | 'columns'
  > = useMemo(
    () => ({
      state: {
        columnVisibility: columnVisibilityState,
        sorting: tableSorting,
      },
      getCoreRowModel: getCoreRowModel(),
      onColumnVisibilityChange: setColumnVisibilityState,
      onSortingChange: setTableSorting,
      getSortedRowModel: getSortedRowModel(),
      ...optionsOverride,
      meta: {
        updateXeroAccountCode,
      },
    }),
    [
      columnVisibilityState,
      optionsOverride,
      tableSorting,
      updateXeroAccountCode,
    ],
  );

  const table = useReactTable({
    data: isFetchingAccountCodeMappings
      ? EMPTY_LOADING_ARRAY
      : accountCodeMappings.data || EMPTY_ARRAY,
    columns: columns,
    ...tableOptions,
  });

  const actions = useMemo(() => {
    const saveButton = (
      <Button
        disabled={!canSave}
        loading={isFetchingAccountCodeMappings || isUpdatingAccountCodeMappings}
        onClick={handleSaveData}
      >
        {SAVE_BUTTON_LABEL}
      </Button>
    );

    if (Array.isArray(tableActions)) {
      return [...tableActions, saveButton];
    }

    return tableActions ? [tableActions, saveButton] : [saveButton];
  }, [
    canSave,
    handleSaveData,
    isFetchingAccountCodeMappings,
    isUpdatingAccountCodeMappings,
    tableActions,
  ]);

  return (
    <>
      <Table
        id="account-code-mapping-list"
        table={table}
        loading={isFetchingAccountCodeMappings}
        title={getTableTitle(xeroOrganisation)}
        showHeader={true}
        usePagination={false}
        showErrorState={hasErrorFetchingAccountCodeMappings}
        useSorting
        errorStateProps={{
          id: 'account-code-mapping-list-error',
          title: ERROR_STATE_TITLE,
          description: ERROR_STATE_DESCRIPTION,
          callToAction: <Button onClick={window.location.reload}>Retry</Button>,
        }}
        emptyStateProps={emptyStateProps}
        tableActions={actions}
        wrapperProps={wrapperProps}
      />
      {congratsModalOpen && (
        <XeroCongratsModal
          modalProps={{
            ...modalProps,
            open: congratsModalOpen,
            onOpenChange: setCongratsModalOpen,
          }}
          handleClose={() => setCongratsModalOpen(false)}
        />
      )}
      {confirmationModalOpen && (
        <XeroTaxRatesMismatchModal
          modalProps={{
            ...modalProps,
            open: confirmationModalOpen,
            onOpenChange: setConfirmationModalOpen,
          }}
          handleClose={() => setConfirmationModalOpen(false)}
          onConfirm={() => saveAccountCodeMappings()}
        />
      )}
    </>
  );
};
